import { NavContextItemRegistry } from './nav-context-item-registry.service';
export { NavContextItemRegistry } from './nav-context-item-registry.service';
import { computed, inject, Injectable } from '@angular/core';
import { NavItem, NavItemConfig } from '../models/nav-item.model';
import { AppBaseCoreService } from '../app-base-core.service';
import { RoleService } from '@app/shared/services/roles.service';
import { CurrentAccessNavigationNode } from '@app/shared/models/current-access-profile.model';
import {
  addRoutePrefix,
  AppRoutePrefixService,
} from './app-route-prefix.service';
import { deriveRouteAccess } from './nav-route-access';

const DASHBOARD_GROUP_CODE = 'dashboard';
const WORKFLOW_GROUP_PREFIX = 'workflow:';
// Utilities remain available through contextual links, rather than competing with worklists.
const HIDDEN_WORKFLOW_UTILITY_CODES = new Set([
  'workflow-process-runs',
  'workflow-start-process',
  'workflow-reference-lookup',
]);

// Authored (DB-seeded) top-level nodes hidden from the side rail at the user's request. Their routes
// stay reachable (e.g. Admin is still available via the static "Admin" item); only the rail entries —
// and, for the group nodes, their sub-items — are suppressed.
const HIDDEN_AUTHORED_NODE_CODES = new Set([
  'platform-home',
  'phms-operations',
  'platform-admin',
  ...HIDDEN_WORKFLOW_UTILITY_CODES,
]);

// PHMS module set rendered in the global side-nav, per the Magic Patterns
// side-nav frame for work item 1147. Order is intentional: Dashboard, Reports,
// Import, Scheduling, Admin. Import points at the existing /phms/intake screen;
// staged routes stay visible as their feature stories land.
// The generic Home landing was removed (#1503): each role now lands on its own
// role-scoped dashboard, so the shared Home nav entry no longer earns its place.
//
// Visibility (ADO 1490) is DERIVED from each item's route: filterMenuItems looks up
// the matching FEATURE_ROUTES entry and gates on that route's declared
// permissions/roles — the same definitions RoleGuard and the API enforce — so the
// menu and the access rules cannot drift. An explicit allowedPermissions/allowedRoles
// on an item still overrides the derived value when a bespoke gate is needed.
const STATIC_SIDE_NAV_ITEMS: NavItemConfig[] = [
  {
    displayName: 'Dashboard',
    route: '/phms/dashboard',
    iconName: 'grid_view',
  },
  {
    displayName: 'Reports',
    route: '/phms/reports',
    iconName: 'bar_chart',
  },
  {
    displayName: 'Import',
    route: '/phms/intake',
    iconName: 'file_upload',
  },
  {
    displayName: 'Scheduling',
    route: '/phms/hearing-schedule',
    iconName: 'calendar_today',
  },
  {
    displayName: 'Institution Management',
    route: '/phms/facilities',
    iconName: 'domain',
    testId: 'nav-institution-management',
  },
  {
    displayName: 'Incarcerated Persons',
    route: '/phms/incarcerated-persons',
    iconName: 'person',
    testId: 'nav-incarcerated-persons',
  },
  {
    displayName: 'Participants',
    route: '/phms/participants',
    iconName: 'groups',
    testId: 'nav-participants',
  },
  {
    displayName: 'Admin',
    route: '/phms/admin',
    iconName: 'settings',
  },
];

/**
 * Coordinate navigation events between components
 * This service keeps app-shell navigation state in Angular signals so shell
 * components can react to access-profile changes without owning that mapping.
 */

@Injectable({
  providedIn: 'root',
})
export class NavService extends AppBaseCoreService {
  // Called RoleService for now but much more than that
  userAccess = inject(RoleService);
  private readonly appRoutePrefix = inject(AppRoutePrefixService);
  private readonly contextItemRegistry = inject(NavContextItemRegistry);

  /**
   * Screen-scoped rail items keyed by their registering source (#1530). They render after the
   * static and authored items and pass through the same access gate.
   */
  readonly contextItems = this.contextItemRegistry.items;

  /**
   * Sidenav menu items. Hold them back until the access profile settles so
   * the shell never falls through to fallback navigation.
   */
  sideNavMenuItems = computed(() => {
    if (!this.userAccess.hasResolvedAccessProfile()) {
      return [];
    }

    const staticItems = this.addRoutePrefixToItems(
      this.filterMenuItems(STATIC_SIDE_NAV_ITEMS),
    );
    const authoredItems = this.mapAuthoredNavigation(
      this.userAccess.navigation(),
    );
    const contextItems = this.addRoutePrefixToItems(
      this.filterMenuItems(
        Object.values(this.contextItems()).flatMap((items) => [...items]),
      ),
    );
    return this.getSidNavMenuItems([
      ...staticItems,
      ...authoredItems,
      ...contextItems,
    ]);
  });

  /**
   * Register (or replace) the rail items a screen contributes while it is open. The items are
   * gated exactly like the static items: give each a `route` (or `accessRoute` when the route
   * carries an id) whose FEATURE_ROUTES entry declares the required permissions.
   */
  setContextItems(sourceKey: string, items: NavItemConfig[]): void {
    this.contextItemRegistry.set(sourceKey, items);
  }

  /** Remove every rail item registered under `sourceKey`. */
  clearContextItems(sourceKey: string): void {
    this.contextItemRegistry.clear(sourceKey);
  }

  /**
   * Dashboard menu items (rubik's cube) use the same settled access state as
   * the side nav, but stay intentionally small in this slice.
   */
  dashboardMenuItems = computed(() => {
    if (!this.userAccess.hasResolvedAccessProfile()) {
      return [];
    }

    return this.collectDashboardNavigation(this.userAccess.navigation());
  });

  constructor() {
    super();
  }
  /**
   * Combine pages from DB with some hard-coded stuff.
   * @param pages
   * @returns
   */
  public getSidNavMenuItems(pages: NavItemConfig[]) {
    const navItems = pages.map((navItemConfig) => new NavItem(navItemConfig));
    const routePrefix = this.appRoutePrefix.routePrefix;

    // Mark routes for this app so the template knows to use routerLink instead of href.
    navItems.forEach((navItem) => {
      navItem.baseHref = routePrefix;
      navItem.isThisApp = this.appRoutePrefix.isAppRoute(navItem.route);
    });

    return navItems;
  }

  private mapAuthoredNavigation(
    nodes: readonly CurrentAccessNavigationNode[],
  ): NavItemConfig[] {
    return nodes.flatMap((node) => {
      // Suppress the hidden authored nodes (and, for groups, their whole subtree) from the rail.
      if (HIDDEN_AUTHORED_NODE_CODES.has(this.normalizeCode(node.nodeCode))) {
        return [];
      }

      const children = this.mapAuthoredNavigation(node.children ?? []);
      const route = this.normalizeAuthoredRoute(node.routePath);
      if (!route && children.length === 0) {
        return [];
      }

      return [
        {
          displayName: node.displayName,
          route,
          iconName: node.iconKey ?? undefined,
          groupCode: node.groupCode ?? undefined,
          children: children.length > 0 ? children : undefined,
          extraProps: {
            nodeCode: node.nodeCode,
            nodeTypeCode: node.nodeTypeCode,
            sortOrder: node.sortOrder,
          },
        },
      ];
    });
  }

  private collectDashboardNavigation(
    nodes: readonly CurrentAccessNavigationNode[],
  ): NavItemConfig[] {
    const seenNodeCodes = new Set<string>();
    const seenRoutes = new Set<string>();
    const compareNodes = (
      left: CurrentAccessNavigationNode,
      right: CurrentAccessNavigationNode,
    ) =>
      left.sortOrder - right.sortOrder ||
      left.displayName.localeCompare(right.displayName);
    const dashboardItems = (
      routeNodes: readonly CurrentAccessNavigationNode[],
    ): NavItemConfig[] =>
      [...routeNodes].sort(compareNodes).flatMap((node) => {
        if (this.normalizeCode(node.groupCode) !== DASHBOARD_GROUP_CODE)
          return [];
        const normalizedNodeCode = this.normalizeCode(node.nodeCode);
        const route = this.normalizeAuthoredRoute(node.routePath);
        if (
          !route ||
          seenNodeCodes.has(normalizedNodeCode) ||
          seenRoutes.has(route)
        )
          return [];
        seenNodeCodes.add(normalizedNodeCode);
        seenRoutes.add(route);
        return [
          {
            displayName: node.displayName,
            route,
            iconName: node.iconKey ?? undefined,
            groupCode: node.groupCode ?? undefined,
            extraProps: { nodeCode: node.nodeCode },
          },
        ];
      });
    const candidates: CurrentAccessNavigationNode[] = [];
    const visit = (items: readonly CurrentAccessNavigationNode[]) => {
      for (const node of items) {
        if (
          HIDDEN_WORKFLOW_UTILITY_CODES.has(this.normalizeCode(node.nodeCode))
        )
          continue;
        if (
          this.normalizeCode(node.nodeCode).startsWith(WORKFLOW_GROUP_PREFIX) &&
          this.normalizeCode(node.nodeTypeCode) === 'group'
        ) {
          candidates.push(node);
        } else {
          if (this.normalizeCode(node.groupCode) === DASHBOARD_GROUP_CODE)
            candidates.push(node);
          visit(node.children ?? []);
        }
      }
    };
    visit(nodes);
    return candidates.sort(compareNodes).flatMap((node) => {
      if (
        !this.normalizeCode(node.nodeCode).startsWith(WORKFLOW_GROUP_PREFIX) ||
        this.normalizeCode(node.nodeTypeCode) !== 'group'
      )
        return dashboardItems([node]);
      const children = dashboardItems(
        this.flattenNavigation(node.children ?? []),
      );
      return children.length > 0
        ? [
            {
              displayName: node.displayName,
              iconName: node.iconKey ?? 'account_tree',
              children,
              extraProps: { nodeCode: node.nodeCode },
            },
          ]
        : [];
    });
  }

  private filterMenuItems(items: NavItemConfig[]): NavItemConfig[] {
    return items.flatMap((item) => {
      const explicitRoles = Array.isArray(item['allowedRoles'])
        ? (item['allowedRoles'] as string[])
        : [];
      const explicitPermissions = Array.isArray(item['allowedPermissions'])
        ? (item['allowedPermissions'] as string[])
        : [];
      const requiredEntitlements = Array.isArray(item['allowedEntitlements'])
        ? (item['allowedEntitlements'] as string[])
        : [];
      const featureCode =
        typeof item['featureFlag'] === 'string'
          ? (item['featureFlag'] as string)
          : '';

      // Derive access from the item's route so the menu tracks the route table's
      // declared permissions/roles (ADO 1490). An explicit allowedRoles/
      // allowedPermissions on the item overrides the derived value. A route that
      // carries runtime values (e.g. an entity id) names its route-table path in
      // accessRoute so the lookup still finds the declared permissions.
      const accessRoute =
        typeof item.accessRoute === 'string' && item.accessRoute.trim()
          ? item.accessRoute
          : item.route;
      const derivedAccess = accessRoute
        ? deriveRouteAccess(accessRoute, this.appRoutePrefix.routePrefix)
        : undefined;
      const requiredRoles =
        explicitRoles.length > 0 ? explicitRoles : (derivedAccess?.roles ?? []);
      const requiredPermissions =
        explicitPermissions.length > 0
          ? explicitPermissions
          : (derivedAccess?.permissions ?? []);

      if (
        requiredRoles.length > 0 &&
        !this.userAccess.hasAnyRole(requiredRoles)
      ) {
        return [];
      }

      if (
        requiredPermissions.length > 0 &&
        !this.userAccess.hasAnyPermission(requiredPermissions)
      ) {
        return [];
      }

      if (
        requiredEntitlements.length > 0 &&
        !this.userAccess.hasAnyEntitlement(requiredEntitlements)
      ) {
        return [];
      }

      const feature = featureCode
        ? this.userAccess.getFeatureToggle(featureCode)
        : undefined;
      if (featureCode && feature && !feature.enabledBool) {
        return [];
      }

      const children = Array.isArray(item.children)
        ? this.filterMenuItems(item.children as NavItemConfig[])
        : undefined;

      if (
        Array.isArray(item.children) &&
        (!children || children.length === 0) &&
        !item.route &&
        !item.url
      ) {
        return [];
      }

      return [
        {
          ...item,
          children,
        },
      ];
    });
  }

  private flattenNavigation(
    nodes: readonly CurrentAccessNavigationNode[],
  ): CurrentAccessNavigationNode[] {
    return nodes.flatMap((node) => [
      node,
      ...this.flattenNavigation(node.children ?? []),
    ]);
  }

  private normalizeAuthoredRoute(
    routePath: string | null | undefined,
  ): string | undefined {
    const trimmed = routePath?.trim();
    if (!trimmed) {
      return undefined;
    }

    return addRoutePrefix(trimmed, this.appRoutePrefix.routePrefix);
  }

  private normalizeCode(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  private addRoutePrefixToItems(items: NavItemConfig[]): NavItemConfig[] {
    return items.map((item) => ({
      ...item,
      route: item.route
        ? addRoutePrefix(item.route, this.appRoutePrefix.routePrefix)
        : item.route,
      children: Array.isArray(item.children)
        ? this.addRoutePrefixToItems(item.children as NavItemConfig[])
        : item.children,
    }));
  }
}
